import React, { useState } from "react";
import { icon } from "../../../config/constants";
import { VARIABLE_ICONS } from "../../Workflow/WorkflowVariables/workflowVariables.constants";
import { AdvTextComponent, TableFormField, TextComponent } from "../FormFieldType";
import { FORM_ICON, FORM_ICONS_UPDATED } from "../FormsConstant";

/**
 * ColumnDropZone Component
 * Individual grid cell within a row that can accept dropped components
 * @param {number} rowIndex - Index of this row in the grid
 * @param {number} columnIndex - Index of this column in the row
 * @param {Object} element - Element currently in this cell (if any)
 * @param {Function} onDrop - Callback for handling component drops
 * @param {Function} onElementSelect - Callback to select an element
 * @param {Object} selectedElement - Currently selected element
 * @param {Function} onElementUpdate - Callback to update element data
 * @param {Function} onElementDelete - Callback to delete element from grid cell
 */
const ColumnDropZone = ({ rowIndex = 0, columnIndex, element, onDrop, onElementSelect, selectedElement, onElementUpdate, onElementDelete }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  // Handle drag over
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  // Handle drag leave
  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  // Handle drop
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    try {
      const componentData = event.dataTransfer.getData("component");
      if (componentData) {
        const droppedComponent = JSON.parse(componentData);
        onDrop(droppedComponent);
      }
    } catch (error) {
      console.error("Error handling drop:", error);
    }
  };

  // Handle element click
  const handleElementClick = (event) => {
    if (element) {
      event.stopPropagation();
      onElementSelect(element);
    }
  };

  // Handle element delete
  const handleElementDelete = (event) => {
    event.stopPropagation();
    if (onElementDelete && element) {
      onElementDelete(element.id, rowIndex, columnIndex);
    }
  };

  // Render the element if it exists
  const renderElement = () => {
    if (!element) return null;

    const isSelected = selectedElement?.id === element.id;
    const isTextLabel = element.type === "text-label";
    const isAdvancedText = element.type === "advanced-text";
    const isTable = element.type === "table";

    return (
      <div className={`column-element ${isSelected ? "selected" : ""}`} onClick={handleElementClick} data-element-id={element.id}>
        <div className='element-preview d-flex element-header align-items-center position-relative'>
          <div className={`preview-placeholder ${isTextLabel || isAdvancedText ? "w-95" : ""} mr-2`}>
            {isTextLabel ? (
              <TextComponent
                element={element}
                onFieldPropertiesChange={onElementUpdate}
                isSelected={isSelected}
                onLabelChange={(elementId, value) => onElementUpdate(elementId, { value })}
              />
            ) : isAdvancedText ? (
              <AdvTextComponent
                element={element}
                isSelected={isSelected}
                onLabelChange={(elementId, value) => onElementUpdate(elementId, { value })}
              />
            ) : isTable ? (
              <TableFormField element={element} onFieldPropertiesChange={onElementUpdate} isSelected={isSelected} />
            ) : element.properties?.isFormDimensionDisplay ? (
              <div className='d-flex align-items-center'>
                <span className='element-type-icon mr-2'>{VARIABLE_ICONS[element.type] || VARIABLE_ICONS.text}</span>
                {element.name}
              </div>
            ) : (
              // Fallback for other element types
              <div className='d-flex align-items-center'>
                <span className='element-type-icon mr-3 d-flex align-items-center'>
                  {element.properties?.chipType === "smart chip"
                    ? VARIABLE_ICONS[element.type]
                    : FORM_ICONS_UPDATED[element.type]}
                </span>
                {element.name}
              </div>
            )}
          </div>

          {/* Delete button - shows on hover */}
          <span className='grid-element-delete-icon' onClick={handleElementDelete} title='Remove from grid'>
            {icon.CLOSE}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`column-drop-zone ${isDragOver ? "drag-over" : ""} ${element ? "has-element" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      {element ? (
        renderElement()
      ) : (
        <div className='drop-placeholder'>
          <div className='drop-icon'>{icon.PLUS || "+"}</div>
          <span className='drop-text'>Drop component here</span>
        </div>
      )}
    </div>
  );
};

export default ColumnDropZone;
